import type { VisualSignalEvent } from '../signals/VisualSignal.js';
import type { VisualSignalBus } from '../signals/VisualSignalBus.js';
import {
  eventMutation,
  resolveLayer,
  sustainedMutations,
  type VisualLayerConfig,
} from '../layers/VisualLayer.js';
import { ArtState, type ArtStateValues } from './ArtState.js';
import { type QualityContext, qualityContext } from '../render/RenderSettings.js';
import { CompositeVisualField } from '../field/SharedVisualField.js';
import { SpatialPlanner, type SpatialMode } from '../wave/SpatialPlanner.js';
import { WaveDirector } from '../wave/WaveDirector.js';
import { waveRecord, type WaveEvent, type WaveRecord, type WaveStyle } from '../wave/WaveEvent.js';
import type { VisualMutation } from './VisualMutation.js';
import {
  resolveWaveSettings,
  type VisualRecipe,
  type WaveSettings,
} from '../recipe/VisualRecipe.js';
import { VisualRandom, mixSeed } from './VisualRandom.js';
import type {
  VisualFrameContext,
  VisualRenderer,
  VisualRendererFactory,
} from './VisualRenderer.js';

/**
 * Drives the layers.
 *
 * The engine is the *only* thing that knows a recipe and a set of renderers can
 * be brought together, and it is deliberately small: apply a recipe, route
 * signals, step every layer, draw every layer. It holds no three objects, opens
 * no files and reads no clock of its own — time arrives in `update`, from the
 * runtime, which got it from `MusicClock`.
 *
 * **A renderer may not take the game down with it.** This is the property the
 * whole plugin structure rests on. Renderers are where experimental work will
 * happen; a half-finished smoke shader that throws on its third frame must cost
 * its own layer and nothing else. So every call into a renderer is wrapped, and
 * a layer that throws is disabled, counted and reported rather than retried
 * every frame at 60 Hz.
 */

export interface VisualArtEngineOptions {
  readonly bus: VisualSignalBus;
  /** One factory per renderer type. Types absent from this map are skipped. */
  readonly factories: Readonly<Record<string, VisualRendererFactory>>;
  /** Faults tolerated from one layer before it is switched off for the run. */
  readonly faultLimit?: number;
  /**
   * Hard ceiling on layers a recipe may build.
   *
   * Phase 2 measured eight layers at a p95 frame time of 53.9 ms — playable
   * frame rate, not judging accuracy, is what suffers first, and `RANDOM` mode
   * can now generate stacks on its own. Layers beyond the cap are dropped, in
   * z-order, and reported.
   */
  readonly maxActiveLayers?: number;
}

interface LiveLayer {
  config: VisualLayerConfig;
  readonly renderer: VisualRenderer;
  readonly random: VisualRandom;
  initialized: boolean;
  faults: number;
  disabledByFault: boolean;
  lastError: string | null;
  /**
   * The context this layer was last updated with.
   *
   * Kept per layer rather than one for the engine because `render` and
   * `handleSignal` must see the same resolved state `update` saw — layers
   * resolve their mapping differently, so one shared context would hand every
   * renderer the last layer's settings.
   */
  lastFrame: VisualFrameContext | null;
}

export interface VisualLayerReport {
  readonly id: string;
  readonly renderer: string;
  readonly enabled: boolean;
  readonly zDepth: number;
  readonly opacity: number;
  readonly intensity: number;
  readonly seed: number;
  readonly faults: number;
  readonly disabledByFault: boolean;
  readonly lastError: string | null;
}

/**
 * How many spawns the log keeps.
 *
 * Long enough that a determinism test can compare a whole passage, bounded
 * because a four-minute song at a note a beat would otherwise accumulate
 * thousands of records nobody reads.
 */
const WAVE_LOG_LIMIT = 512;

export class VisualArtEngine {
  private readonly bus: VisualSignalBus;
  private readonly factories: Readonly<Record<string, VisualRendererFactory>>;
  private readonly faultLimit: number;
  private readonly maxActiveLayers: number;
  private readonly art = new ArtState();
  private quality: QualityContext = qualityContext('LIVE');
  /** Layers a recipe asked for that the cap refused. */
  private droppedForCapacity = 0;
  /**
   * Which layers the cap refused, by renderer and id.
   *
   * A count alone was not enough. A recipe naming seven materials against a
   * cap of six lost one of them silently — the picture was simply missing a
   * material and nothing said which, or that anything had happened at all.
   */
  private readonly droppedLayers: string[] = [];
  private mutationsDispatched = 0;

  private layers: LiveLayer[] = [];
  private recipe: VisualRecipe | null = null;
  private unsubscribe: (() => void) | null = null;
  private enabled = true;
  private lastContext: VisualFrameContext | null = null;
  /** Renderer types a recipe asked for that no factory can build. */
  private readonly unknownRenderers = new Set<string>();
  /**
   * Why a renderer could not be constructed.
   *
   * Recorded rather than logged: this project builds the visual core with
   * `lib: ["ES2022"]` and `types: []`, so `console` is not in scope here — which
   * is the same rule that keeps `three` and the DOM out. Faults travel out
   * through `diagnostics()` and the runtime decides whether to print them.
   */
  private readonly buildFailures: string[] = [];

  // --- the wave layer ------------------------------------------------------
  //
  // Three decisions live here rather than in a renderer: whether a signal is
  // worth a disturbance, what material it comes out as, and where it lands.
  // A renderer is told the answer and draws it. That is what lets the same
  // press be smoke on one bar and ink on the next without any renderer
  // knowing the other exists.
  /**
   * What the layers have published about the picture, for the layers to read.
   *
   * Held by the engine rather than passed between renderers so that no
   * renderer ever holds a reference to another. See `SharedVisualField`.
   */
  private readonly fields = new CompositeVisualField();
  private readonly planner = new SpatialPlanner();
  private readonly director = new WaveDirector({ mode: 'FIXED', available: [] });
  private waveSettings: WaveSettings = resolveWaveSettings(null);
  /**
   * The stream every wave decision is drawn from.
   *
   * Its own stream, not a layer's. A layer's stream is taken by whatever that
   * renderer happens to draw this frame, so sharing it would make the material
   * sequence depend on how much work an unrelated renderer did — and the whole
   * claim of this phase is that the sequence depends only on the seed.
   */
  private waveRandom: VisualRandom = new VisualRandom(0);
  private waveIndex = 0;
  private unroutedWaves = 0;
  /** Identifies a group of materials answering one disturbance. */
  private collisionId = 0;
  /** The spawn log the determinism tests compare. Capped; see `WAVE_LOG_LIMIT`. */
  private readonly waveSpawns: WaveRecord[] = [];

  constructor(options: VisualArtEngineOptions) {
    this.bus = options.bus;
    this.factories = options.factories;
    this.faultLimit = options.faultLimit ?? 3;
    this.maxActiveLayers = Math.max(1, options.maxActiveLayers ?? 6);
    this.unsubscribe = this.bus.subscribe('*', (event) => this.routeSignal(event));
  }

  get activeRecipe(): VisualRecipe | null {
    return this.recipe;
  }

  /**
   * Switches the whole stack.
   *
   * Renderers are rebuilt rather than reconfigured. Reusing an instance across
   * recipes would carry its accumulated state into a configuration that never
   * produced it, which is exactly the kind of thing that makes a seed stop
   * reproducing.
   */
  applyRecipe(recipe: VisualRecipe): void {
    this.disposeLayers();
    this.recipe = recipe;
    this.unknownRenderers.clear();
    this.resetWaves(recipe);

    const sorted = [...recipe.layers].sort((a, b) => a.zDepth - b.zDepth);
    // The cap keeps the bottom of the stack, because that is where the piece's
    // ground normally is; dropping MANDALA to keep a decorative top layer would
    // be the wrong half to lose.
    const ordered = sorted.slice(0, this.maxActiveLayers);
    this.droppedForCapacity = sorted.length - ordered.length;
    this.droppedLayers.length = 0;
    for (const config of sorted.slice(this.maxActiveLayers)) {
      this.droppedLayers.push(`${config.renderer}:${config.id}`);
      // Recorded where the build failures go, because from the outside this
      // is the same kind of problem: the recipe asked for something and the
      // frame does not contain it.
      this.buildFailures.push(
        `${config.renderer}:${config.id} dropped — recipe has ${sorted.length} layers, ` +
          `cap is ${this.maxActiveLayers}`,
      );
    }

    for (const config of ordered) {
      const factory = this.factories[config.renderer];
      if (factory === undefined) {
        this.unknownRenderers.add(config.renderer);
        continue;
      }
      // Derived from the recipe seed and the layer's identity, so adding a layer
      // cannot shift the numbers an existing one draws.
      const seed = mixSeed(recipe.seed, `${config.renderer}:${config.id}:${config.seed}`);
      try {
        const renderer = factory(config.id, seed);
        this.layers.push({
          config,
          renderer,
          random: new VisualRandom(seed),
          initialized: false,
          faults: 0,
          disabledByFault: false,
          lastError: null,
          lastFrame: null,
        });
      } catch (cause) {
        this.unknownRenderers.add(config.renderer);
        this.buildFailures.push(
          `${config.renderer}: ${cause instanceof Error ? cause.message : String(cause)}`,
        );
      }
    }

    // Only materials that actually have a layer are offered. A weighting table
    // may name six and the recipe may carry two; picking one of the other four
    // would produce a wave with nowhere to go, which is a silent gap in the
    // picture rather than an error anyone would notice.
    this.director.configure({
      mode: this.waveSettings.waveMode,
      minMaterials: this.waveSettings.collision.minMaterials,
      maxMaterials: this.waveSettings.collision.maxMaterials,
      available: [...new Set(this.layers.map((live) => live.config.renderer))],
      weights: this.waveSettings.waveStyles,
    });
  }

  /** Wave state belongs to a run on a recipe, so it is rebuilt with one. */
  private resetWaves(recipe: VisualRecipe | null): void {
    this.waveSettings = resolveWaveSettings(recipe);
    this.planner.reset();
    this.director.reset();
    this.waveRandom = new VisualRandom(mixSeed(recipe?.seed ?? 0, 'wave'));
    this.waveIndex = 0;
    this.collisionId = 0;
    this.unroutedWaves = 0;
    this.waveSpawns.length = 0;
  }

  /** Changes one layer's settings without rebuilding its renderer. */
  updateLayer(id: string, patch: Partial<VisualLayerConfig>): boolean {
    const live = this.layers.find((entry) => entry.config.id === id);
    if (live === undefined) return false;
    live.config = { ...live.config, ...patch };
    if (patch.parameters !== undefined) {
      this.guard(live, 'setParameters', () => live.renderer.setParameters(live.config.parameters));
    }
    if (this.recipe !== null) {
      this.recipe = {
        ...this.recipe,
        layers: this.recipe.layers.map((entry) => (entry.id === id ? live.config : entry)),
      };
    }
    return true;
  }

  /**
   * Turns the whole engine off.
   *
   * The regression baseline: with the visual engine disabled the game must draw
   * what it drew before any of this existed.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    for (const live of this.layers) {
      this.guard(live, 'setEnabled', () => live.renderer.setEnabled(enabled && live.config.enabled));
    }
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Sets the quality every layer is drawn at.
   *
   * Held rather than only forwarded, because layers built later — by a recipe
   * applied after this call — must be built at the same quality. Without that a
   * mid-render recipe change would silently drop back to LIVE.
   */
  setQuality(quality: QualityContext): void {
    this.quality = quality;
    for (const live of this.layers) {
      const apply = live.renderer.setQuality;
      if (apply === undefined) continue;
      this.guard(live, 'setQuality', () => apply.call(live.renderer, quality));
    }
  }

  get currentQuality(): QualityContext {
    return this.quality;
  }

  /** A new run on the same recipe: state clears, seeds do not. */
  reset(): void {
    this.art.reset();
    this.mutationsDispatched = 0;
    this.resetWaves(this.recipe);
    for (const live of this.layers) {
      live.faults = 0;
      live.disabledByFault = false;
      live.lastError = null;
      this.guard(live, 'reset', () => live.renderer.reset());
    }
  }

  /**
   * One frame.
   *
   * Advancing the bus is the engine's job because the envelopes must decay
   * exactly once per frame; a renderer calling `advance` itself would consume
   * the frame its neighbours were about to read.
   */
  update(time: number, deltaTime: number): void {
    const signals = this.bus.advance(deltaTime);
    // Advanced even when the engine is off, so that turning it back on does not
    // resume a state frozen from minutes ago.
    this.art.advance(deltaTime, signals, time);
    if (!this.enabled) return;

    const art = this.art.current;
    for (const live of this.layers) {
      if (!this.isLive(live)) continue;
      const context: VisualFrameContext = {
        time,
        deltaTime,
        signals,
        layer: resolveLayer(live.config, signals),
        random: live.random,
        art,
        field: this.fields,
      };
      this.lastContext = context;
      if (!live.initialized) {
        const ok = this.guard(live, 'initialize', () => live.renderer.initialize(context));
        if (!ok) continue;
        live.initialized = true;
        // Whatever this renderer can say about the picture becomes readable
        // by the others from the next frame on.
        const reader = live.renderer.fieldReader;
        if (reader !== undefined) this.fields.publish(live.config.id, reader);
        const setQuality = live.renderer.setQuality;
        if (setQuality !== undefined) {
          this.guard(live, 'setQuality', () => setQuality.call(live.renderer, this.quality));
        }
        this.guard(live, 'setParameters', () => live.renderer.setParameters(context.layer.parameters));
        this.guard(live, 'setEnabled', () => live.renderer.setEnabled(true));
      }
      live.lastFrame = context;
      this.guard(live, 'update', () => live.renderer.update(context));

      // Level signals mapped to actions are re-applied every frame; event
      // signals fire once, from `routeSignal`.
      for (const item of sustainedMutations(live.config, signals, time)) {
        this.dispatch(live, item, context);
      }
    }
  }

  /**
   * Sends one mutation to one layer, and folds it into the artwork's state.
   *
   * The state is updated whether or not the renderer does anything with the
   * action. A recipe that asks for `SPLASH` from a renderer that has no splash
   * has still said something about the piece, and `ArtState` is where that is
   * recorded — which is also what keeps the state meaningful across a recipe
   * change mid-run.
   */
  private dispatch(live: LiveLayer, item: VisualMutation, context: VisualFrameContext): void {
    this.mutationsDispatched += 1;
    this.art.applyMutation(item);
    const apply = live.renderer.applyMutation;
    if (apply === undefined) return;
    this.guard(live, `applyMutation:${item.action}`, () =>
      apply.call(live.renderer, item, context),
    );
  }

  /** The artwork's condition, for renderers, the HUD and the tests. */
  get artState(): ArtStateValues {
    return this.art.current;
  }

  /** A press reached the piece. See `ArtState.registerInput`. */
  registerInput(songTime: number, judged: boolean, onBeatDistance: number): void {
    this.art.registerInput(songTime, judged, onBeatDistance);
  }

  registerComboBreak(): void {
    this.art.registerComboBreak();
  }

  /** Draws every live layer, in z-depth order. */
  render(): void {
    if (!this.enabled) return;
    for (const live of this.layers) {
      if (!this.isLive(live) || !live.initialized) continue;
      const context = live.lastFrame;
      if (context === null) continue;
      this.guard(live, 'render', () => live.renderer.render(context));
    }
  }

  /**
   * Called once the frame is on screen. See `VisualRenderer.postRender`.
   *
   * Separate from `render` because `render` still happens *before* the stage
   * composites, which is exactly the ordering this exists to avoid.
   */
  postRender(): void {
    if (!this.enabled) return;
    for (const live of this.layers) {
      if (!this.isLive(live) || !live.initialized) continue;
      const after = live.renderer.postRender;
      if (after === undefined) continue;
      const context = live.lastFrame;
      if (context === null) continue;
      this.guard(live, 'postRender', () => after.call(live.renderer, context));
    }
  }

  private routeSignal(event: VisualSignalEvent): void {
    if (!this.enabled) return;
    const consumed = this.routeWave(event);
    for (const live of this.layers) {
      if (!this.isLive(live) || !live.initialized) continue;
      const context = live.lastFrame ?? this.lastContext;
      if (context === null) continue;

      // A signal the wave layer took is not also delivered raw. Otherwise a
      // note would produce both the wave it was turned into and whatever each
      // renderer independently makes of `NOTE_HIT`, which is two marks for one
      // press and makes the chosen material impossible to see.
      const handler = live.renderer.handleSignal;
      if (handler !== undefined && !consumed) {
        this.guard(live, 'handleSignal', () => handler.call(live.renderer, event, context));
      }

      const item = eventMutation(
        live.config,
        event.name,
        event.strength,
        event.time,
        event.detail,
      );
      if (item !== null) this.dispatch(live, item, context);
    }
  }

  /**
   * Turns a signal into a wave, if this recipe asked for waves.
   *
   * Returns whether the signal was consumed. Most are not: the bar and the
   * section move the composition rather than making a mark, and everything
   * else falls through to the renderers untouched.
   */
  private routeWave(event: VisualSignalEvent): boolean {
    if (!this.waveSettings.enabled) return false;

    switch (event.name) {
      case 'BAR':
        // The composition moves on. Not a mark — a bar that spawned something
        // would just be a slower beat.
        this.planner.shift(this.waveRandom);
        return false;

      case 'SECTION':
        // The tendency changes. Also not a mark.
        this.director.retune(this.waveRandom);
        return false;

      case 'NOTE_HIT':
        this.emitWave(event, 'struck', null);
        return true;

      case 'NOTE_MISS':
        // Harder, and thrown across the field. A miss is not a weaker hit; it
        // is the thing that breaks the arrangement the run was building.
        this.emitWave(event, 'torn', 'OPPOSITE_SIDE');
        return true;

      case 'PLAYER_INPUT': {
        // Only the presses that judged nothing. A press that landed on a note
        // already produced its wave through NOTE_HIT, and turning it into a
        // second one would double every mark in a clean run.
        if (event.detail.judged === true) return true;
        this.emitWave(event, 'stray', 'AVOID_PREVIOUS');
        return true;
      }

      case 'CUSTOM_EVENT':
        if (event.detail.kind !== 'comboBreak') return false;
        this.emitWave(event, 'torn', 'OPPOSITE_SIDE');
        return true;

      default:
        return false;
    }
  }

  /**
   * Builds one wave and hands it to whichever layer draws that material.
   *
   * The order of the draws matters and is fixed: material, then position. Two
   * runs that agree on the inputs take the same numbers in the same order from
   * the same stream, which is what makes the spawn log reproducible.
   */
  private emitWave(
    event: VisualSignalEvent,
    style: WaveStyle,
    override: SpatialMode | null,
  ): void {
    const context = this.lastContext;
    if (context === null) return;

    // Every material answering this one disturbance. One outside COLLISION.
    const materials = this.director.chooseMany(this.waveRandom, context.signals);
    if (materials.length === 0) {
      this.unroutedWaves += 1;
      return;
    }

    // A long combo grows where it already is; everything else follows the
    // recipe unless the signal itself asked for somewhere specific.
    const mode: SpatialMode =
      override ??
      (this.art.current.flow > 0.7 ? 'FOLLOW_PREVIOUS' : this.waveSettings.spatialMode);

    // Placed once for the whole group. Every material in a collision has to
    // answer the *same* disturbance, so asking the planner once per material
    // would scatter them and there would be nothing to collide.
    const placement = this.planner.place({
      mode,
      random: this.waveRandom,
      signals: context.signals,
      deltaTime: context.deltaTime,
    });

    const strength = clampUnit(event.strength);
    const collisionId = this.collisionId;
    if (materials.length > 1) this.collisionId += 1;

    let delivered = 0;
    for (let position = 0; position < materials.length; position += 1) {
      const material = materials[position] as string;
      const index = this.waveIndex;
      this.waveIndex += 1;

      const wave: WaveEvent = {
        index,
        time: event.time,
        x: placement.x,
        y: placement.y,
        direction: placement.direction,
        strength,
        scale: style === 'torn' ? 1.35 : style === 'stray' ? 0.75 : 1,
        lifetime: 2.5 + strength * 2.5,
        // Derived from the run seed and this event's index, so a material may
        // take as many numbers as it likes without moving the next decision.
        // Each member of a collision gets its own, so two materials answering
        // one disturbance do not draw the identical shape in the same place.
        seed: mixSeed(this.recipe?.seed ?? 0, `wave:${index}`),
        material,
        style,
        source: event.name,
        placement: placement.mode,
        ...(materials.length > 1
          ? { collision: { id: collisionId, materials, index: position } }
          : {}),
      };

      this.waveSpawns.push(waveRecord(wave));
      while (this.waveSpawns.length > WAVE_LOG_LIMIT) this.waveSpawns.shift();

      let landed = false;
      for (const live of this.layers) {
        if (live.config.renderer !== material) continue;
        if (!this.isLive(live) || !live.initialized) continue;
        const spawn = live.renderer.spawnWave;
        if (spawn === undefined) continue;
        const frame = live.lastFrame ?? context;
        if (this.guard(live, 'spawnWave', () => spawn.call(live.renderer, wave, frame))) {
          landed = true;
        }
      }
      if (landed) delivered += 1;
      else this.unroutedWaves += 1;
    }
    void delivered;
  }

  /** The spawn log, oldest first. See `WaveRecord`. */
  get waveLog(): readonly WaveRecord[] {
    return this.waveSpawns;
  }

  private isLive(live: LiveLayer): boolean {
    return live.config.enabled && !live.disabledByFault;
  }

  /**
   * Runs one renderer call and contains anything it throws.
   *
   * Returns whether the call succeeded, so a caller can skip work that depended
   * on it. After `faultLimit` faults the layer stops being called at all: a
   * renderer that throws once a frame would otherwise fill the console and cost
   * more than it draws.
   */
  private guard(live: LiveLayer, what: string, call: () => void): boolean {
    try {
      call();
      return true;
    } catch (cause) {
      live.faults += 1;
      live.lastError = `${what}: ${cause instanceof Error ? cause.message : String(cause)}`;
      if (live.faults >= this.faultLimit) live.disabledByFault = true;
      return false;
    }
  }

  layerReports(): readonly VisualLayerReport[] {
    return this.layers.map((live) => ({
      id: live.config.id,
      renderer: live.config.renderer,
      enabled: live.config.enabled,
      zDepth: live.config.zDepth,
      opacity: live.config.opacity,
      intensity: live.config.intensity,
      seed: live.config.seed,
      faults: live.faults,
      disabledByFault: live.disabledByFault,
      lastError: live.lastError,
    }));
  }

  diagnostics(): {
    readonly enabled: boolean;
    readonly renderMode: string | null;
    readonly seed: number | null;
    readonly layers: readonly VisualLayerReport[];
    readonly unknownRenderers: readonly string[];
    readonly buildFailures: readonly string[];
    readonly available: readonly string[];
    readonly art: ArtStateValues;
    readonly mutationsDispatched: number;
    readonly droppedForCapacity: number;
    readonly droppedLayers: readonly string[];
    readonly maxActiveLayers: number;
    readonly bus: ReturnType<VisualSignalBus['diagnostics']>;
    readonly waves: {
      readonly enabled: boolean;
      readonly emitted: number;
      readonly unrouted: number;
      readonly director: ReturnType<WaveDirector['diagnostics']>;
      readonly fieldProviders: readonly string[];
      readonly spatial: ReturnType<SpatialPlanner['diagnostics']>;
      readonly spatialMode: SpatialMode;
    };
  } {
    return {
      enabled: this.enabled,
      renderMode: this.recipe?.renderMode ?? null,
      seed: this.recipe?.seed ?? null,
      layers: this.layerReports(),
      unknownRenderers: [...this.unknownRenderers],
      buildFailures: [...this.buildFailures],
      available: Object.keys(this.factories),
      art: this.art.current,
      mutationsDispatched: this.mutationsDispatched,
      droppedForCapacity: this.droppedForCapacity,
      droppedLayers: [...this.droppedLayers],
      maxActiveLayers: this.maxActiveLayers,
      bus: this.bus.diagnostics(),
      waves: {
        enabled: this.waveSettings.enabled,
        emitted: this.waveIndex,
        unrouted: this.unroutedWaves,
        director: this.director.diagnostics(),
        fieldProviders: this.fields.providers,
        spatial: this.planner.diagnostics(),
        spatialMode: this.waveSettings.spatialMode,
      },
    };
  }

  private disposeLayers(): void {
    this.fields.clear();
    for (const live of this.layers) {
      try {
        live.renderer.dispose();
      } catch (cause) {
        // A renderer that cannot even let go is not worth failing a teardown for.
        this.buildFailures.push(
          `${live.config.id} threw while disposing: ` +
            `${cause instanceof Error ? cause.message : String(cause)}`,
        );
      }
    }
    this.layers = [];
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.disposeLayers();
    this.recipe = null;
    this.lastContext = null;
  }
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
